import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import JournalInfo from 'donor-elf-web/lib/models/journal-info';
import JournalContactInfo from 'donor-elf-web/lib/models/journal-contact-info';
import Store from '@ember-data/store';
import JournalStageModel from 'donor-elf-web/models/journal-stage';
import JournalModel from 'donor-elf-web/models/journal';
import JournalContactDetailModel from 'donor-elf-web/models/all-funds/journal-contact-detail';
import ContactModel from 'donor-elf-web/models/contact';
import ContactsCache from './contacts-cache';
import CurrentUser from './current-user';

let PAGE_SIZE = 50;

interface StageInfo {
  stage: JournalStageModel;
  journalInfo: JournalInfo;
}

interface CacheInterface {
  journalInfoById: { [journalId: string]: JournalInfo };
  journalInfosByContactId: { [contactId: string]: JournalInfo[] };
  activeJournalInfosForContact: { [contactId: string]: JournalInfo[] };
  stageInfoById: { [stageId: string]: StageInfo };
  journalContactInfosByJournalId?: {
    [journalId: string]: JournalContactInfo[];
  };
  journalInfoByIdFromOtherFunds?: { [journalId: string]: JournalInfo };
}

export default class JournalService extends Service {
  @service declare contactsCache: ContactsCache;
  @service declare currentUser: CurrentUser;
  @service declare store: Store;

  @tracked _cache: CacheInterface = {
    journalInfoById: {},
    journalInfosByContactId: {},
    activeJournalInfosForContact: {},
    stageInfoById: {},
  };
  _loadPromise?: Promise<void>;

  @tracked journals: JournalModel[] = [];
  @tracked activeJournals: JournalModel[] = [];
  @tracked hasMultipleJournals = false;

  journalInfoById(journalId: string): JournalInfo {
    return this._cache.journalInfoById[journalId];
  }

  get sortedJournalInfos() {
    let sortedJournalInfos = Object.values(this._cache.journalInfoById).sort(
      (a, b) => {
        return a.journal.startDate < b.journal.startDate ? 1 : -1;
      },
    );

    // active journals first
    return [
      ...sortedJournalInfos.filter((x) => x.journal.active),
      ...sortedJournalInfos.filter((x) => !x.journal.active),
    ];
  }

  // use getter property so the tracked properties will correctly break the cache
  get journalInfosByContactId(): { [contactId: string]: JournalInfo[] } {
    return this._cache.journalInfosByContactId;
  }

  activeJournalInfosForContactId(contactId: string): JournalInfo[] {
    let journals = this._cache.activeJournalInfosForContact[contactId];
    if (journals) {
      return journals;
    }
    journals = (this.journalInfosByContactId[contactId] || []).filter(
      (x) => x.journal.active,
    );
    this._cache.activeJournalInfosForContact[contactId] = journals;
    return journals;
  }

  stageInfoById(stageId: string): StageInfo {
    return this._cache.stageInfoById[stageId];
  }

  // Only loads journals for current fund.
  load({ forceReload }: { forceReload?: boolean } = {}): Promise<void> {
    if (forceReload) {
      this._loadPromise = undefined;
    }
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._cache = {
      journalInfoById: {},
      journalInfosByContactId: {},
      activeJournalInfosForContact: {},
      stageInfoById: {},
    };

    return (this._loadPromise = this.store
      .query('journal', {})
      .then((records) => {
        this.journals = records.slice();

        this._cache.journalInfoById = {};
        this._cache.journalInfosByContactId = {};
        this._cache.stageInfoById = {};
        this._cache.activeJournalInfosForContact = {};
        this.journals.forEach((journal) => {
          this.createJournalInfo(journal);
        });
        this._cache = this._cache; // break the cache
        this.activeJournals = this.journals.filter((x) => x.active);
        this.hasMultipleJournals = this.journals.length > 1;
      }));
  }

  createJournalInfo(journal: JournalModel): JournalInfo {
    let journalInfo = new JournalInfo(journal);
    this._cache.journalInfoById[journal.id] = journalInfo;
    journalInfo.sortedStages.forEach((stage) => {
      this._cache.stageInfoById[stage.id] = { stage, journalInfo };
    });
    journal.contactIds.forEach((contactId) => {
      let contactIdString = contactId.toString();
      if (this._cache.journalInfosByContactId[contactIdString] == null) {
        this._cache.journalInfosByContactId[contactIdString] = [];
      }
      this._cache.journalInfosByContactId[contactIdString].push(journalInfo);
    });
    return journalInfo;
  }

  async loadJournalContactInfos(
    journalId: string,
    {
      forceReload,
      pageCallback,
    }: {
      forceReload?: boolean;
      pageCallback?: (args: {
        pageRecords: JournalContactDetailModel[];
        downloadedCount: number;
        totalCount: number;
      }) => void;
    } = {},
  ): Promise<JournalContactInfo[]> {
    if (forceReload) {
      delete this._cache.journalContactInfosByJournalId?.[journalId];
    }
    if (this._cache.journalContactInfosByJournalId?.[journalId] != null) {
      return this._cache.journalContactInfosByJournalId[journalId];
    }
    if (this._cache.journalContactInfosByJournalId == null) {
      this._cache.journalContactInfosByJournalId = {};
    }

    let records: JournalContactInfo[] = [];
    await queryWithPages<JournalContactDetailModel>({
      store: this.store,
      modelName: 'all-funds/journal-contact-detail',
      queryOptions: {
        filter: {
          journal_id: journalId,
        },
      },
      pageSize: PAGE_SIZE,
      pageCallback: (args) => {
        records.push(
          ...args.pageRecords.map((x) => this.createJournalContactInfo(x)),
        );
        pageCallback?.(args);
      },
    });

    return (this._cache.journalContactInfosByJournalId[journalId] = records);
  }

  createJournalContactInfo(
    journalContactDetailModel: JournalContactDetailModel,
  ) {
    let journalInfo = this.journalInfoById(
      journalContactDetailModel.journalContact.journalId,
    );
    let contact =
      this.contactsCache.contactsById[
        journalContactDetailModel.journalContact.contactId
      ];
    // todo: this needs removed but we have to add it when retrieving journals from other funds
    if (contact == null) {
      contact = { id: journalContactDetailModel.journalContact.contactId };
    }
    // ***
    return new JournalContactInfo({
      journalContactDetailModel,
      journalInfo,
      contact,
    });
  }

  removeJournalInfoForContactId(contactId: string) {
    delete this._cache.journalInfosByContactId[contactId];
  }

  getDistinctJournalSegments(journalContactInfos: JournalContactInfo[]) {
    let segments: any = {};
    journalContactInfos.forEach((x) => {
      x.journalContact.segments.forEach(
        (segment) => (segments[segment] = true),
      );
    });
    return Object.keys(segments).sort();
  }

  async addContactToJournal(
    journalId: string,
    contact: ContactModel,
  ): Promise<JournalContactInfo> {
    let record = this.store.createRecord('journal-add-contact', {
      journalId: journalId,
      contactIds: [contact.id],
    });
    await record.save();
    let results = (
      await this.store.query('all-funds/journal-contact-detail', {
        filter: { contact_id: contact.id, journal_id: journalId },
      })
    ).slice();
    let journalContactInfo = this.createJournalContactInfo(results[0]);
    if (this._cache.journalContactInfosByJournalId != null) {
      this._cache.journalContactInfosByJournalId![journalId] = [
        ...this._cache.journalContactInfosByJournalId![journalId],
        journalContactInfo,
      ];
    }
    let journalInfo = this.journalInfoById(journalId);
    if (
      this.journalInfosByContactId[contact.id]?.includes(journalInfo) !== true
    ) {
      journalInfo.journal.contactIds = [
        ...journalInfo.journal.contactIds,
        contact.id,
      ];
      if (this._cache.journalInfosByContactId[contact.id] == null) {
        this._cache.journalInfosByContactId[contact.id] = [];
      }
      this._cache.journalInfosByContactId[contact.id].push(journalInfo);
    }
    this._cache = this._cache; // break the cache
    return journalContactInfo;
  }
}
